import React, { useState, useEffect, useCallback } from "react";
import reportService from "../../services/reportService";
import LoadingSpinner from "../common/LoadingSpinner";
import Modal, { ModalSecondaryButton, ModalPrimaryButton } from "../common/Modal";

// Safety cap, mirrored from ReportService.MAX_REPORTS_PER_LEARNER. A learner can attend
// several classes per semester, each with its own Zeugnis, so this is not a functional limit.
const MAX_REPORTS_PER_LEARNER = 50;

const ReportsList = ({ learnerId, isOwnProfile }) => {
  const [reports, setReports] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setLoadingSemesters] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedSemesterId, setSelectedSemesterId] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [reportToArchive, setReportToArchive] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Default to collapsed
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [showExportConfig, setShowExportConfig] = useState(false);
  const [selectedReportIds, setSelectedReportIds] = useState([]);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      let data;
      if (isOwnProfile) {
        data = await reportService.getMyReports();
      } else {
        if (!learnerId) return; // Should not happen if parent checks
        data = await reportService.getLearnerReports(learnerId);
      }
      setReports(data);
    } catch (err) {
      setError("Failed to load reports.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isOwnProfile, learnerId]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    const loadSemesters = async () => {
      if (!isOwnProfile) return;

      try {
        setLoadingSemesters(true);
        const data = await reportService.getMySemesters();
        setSemesters(data || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load semesters for report upload.");
      } finally {
        setLoadingSemesters(false);
      }
    };

    loadSemesters();
  }, [isOwnProfile]);

  useEffect(() => {
    if (!semesters.length) {
      setSelectedSemesterId("");
      return;
    }

    const selectionExists = semesters.some(
      (semester) => String(semester.id) === String(selectedSemesterId),
    );
    if (!selectionExists) {
      setSelectedSemesterId("");
    }
  }, [semesters, selectedSemesterId]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Only PDF files are allowed.");
      return;
    }

    if (reports.length >= MAX_REPORTS_PER_LEARNER) {
      alert(`Maximum ${MAX_REPORTS_PER_LEARNER} reports allowed.`);
      return;
    }

    if (isOwnProfile && !selectedSemesterId) {
      alert("Please select a semester before uploading the report.");
      return;
    }

    try {
      setUploading(true);
      setError("");
      await reportService.uploadReport(file, selectedSemesterId || null);
      await loadReports();
      setSelectedSemesterId("");
      e.target.value = "";
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.statusText ||
        "Upload failed.";
      setError(msg);
      // alert(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (report) => {
    try {
      if (isOwnProfile) {
        await reportService.downloadMyReport(
          report.id,
          report.originalFilename,
        );
      } else {
        await reportService.downloadLearnerReport(
          learnerId,
          report.id,
          report.originalFilename,
        );
      }
    } catch (err) {
      alert("Download failed");
    }
  };

  // Immediately exports every active report as one ZIP - the main, one-click action.
  const handleExportAll = async () => {
    if (isExporting || activeReports.length === 0) return;
    try {
      setIsExporting(true);
      setExportError("");
      await reportService.exportReportsAsZip(learnerId, isOwnProfile, null);
    } catch (err) {
      setExportError("Could not export reports. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const openExportConfig = () => {
    setSelectedReportIds(activeReports.map((r) => r.id));
    setExportError("");
    setShowExportConfig(true);
  };

  const closeExportConfig = () => {
    if (isExporting) return;
    setShowExportConfig(false);
  };

  const toggleReportSelected = (reportId) => {
    setSelectedReportIds((prev) =>
      prev.includes(reportId)
        ? prev.filter((id) => id !== reportId)
        : [...prev, reportId],
    );
  };

  const handleExportSelected = async () => {
    if (isExporting || selectedReportIds.length === 0) return;
    try {
      setIsExporting(true);
      setExportError("");
      await reportService.exportReportsAsZip(
        learnerId,
        isOwnProfile,
        selectedReportIds,
      );
      setShowExportConfig(false);
    } catch (err) {
      setExportError("Could not export the selected reports. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handlePreview = async (report) => {
    try {
      setPreviewTitle(report.originalFilename);
      const url = await reportService.getPreviewUrl(
        report.id,
        learnerId,
        isOwnProfile,
      );
      setPreviewUrl(url);
    } catch (err) {
      alert("Preview failed");
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewTitle("");
  };

  const handleArchiveClick = (report) => {
    setReportToArchive(report);
  };

  // Archiving keeps the document: it stays available to the Revisor and to staff,
  // it only leaves the learner's active list.
  const confirmArchive = async () => {
    if (!reportToArchive) return;

    try {
      setIsArchiving(true);
      const updated = await reportService.archiveReport(reportToArchive.id);
      setReports((prev) =>
        prev.map((r) =>
          r.id === reportToArchive.id
            ? { ...r, archivedAt: updated?.archivedAt || new Date().toISOString() }
            : r,
        ),
      );

      if (previewTitle && reportToArchive.originalFilename === previewTitle) {
        closePreview();
      }

      setReportToArchive(null);
    } catch (err) {
      alert("Could not archive the report.");
    } finally {
      setIsArchiving(false);
    }
  };

  const handleRestore = async (report) => {
    try {
      await reportService.restoreReport(report.id);
      setReports((prev) =>
        prev.map((r) => (r.id === report.id ? { ...r, archivedAt: null } : r)),
      );
    } catch (err) {
      alert("Could not restore the report.");
    }
  };

  const activeReports = reports.filter((r) => !r.archivedAt);
  const archivedReports = reports.filter((r) => r.archivedAt);

  if (loading && reports.length === 0) return <LoadingSpinner />;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Header - Clickable to toggle */}
      <div
        className="flex justify-between items-center p-6 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <div className="mr-3 text-slate-400">
            <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}></i>
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Reports</h2>
            <p className="text-slate-500 text-sm">
              Manage school reports (PDF only). You see your uploaded reports which you set in the Semester Confirmation.
            </p>
          </div>
        </div>

        {isExpanded && (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {/* Split button: main area exports everything immediately, the small icon
                opens the picker for a custom selection. */}
            <div className="flex rounded-lg overflow-hidden border border-slate-200 shadow-sm">
              <button
                onClick={handleExportAll}
                disabled={isExporting || activeReports.length === 0}
                title="Download all reports as a ZIP archive"
                className="px-3 py-2 bg-white text-slate-700 text-xs font-semibold flex items-center gap-2 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isExporting ? (
                  <i className="fas fa-circle-notch fa-spin"></i>
                ) : (
                  <i className="fas fa-file-zipper"></i>
                )}
                Export all reports
              </button>
              <button
                onClick={openExportConfig}
                disabled={isExporting || activeReports.length === 0}
                title="Choose which reports to export"
                aria-label="Choose which reports to export"
                className="px-2.5 py-2 bg-white text-slate-500 border-l border-slate-200 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i className="fas fa-sliders"></i>
              </button>
            </div>

            {/* Only show Upload button when expanded and owned */}
            {isOwnProfile && (
              <div className="relative">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleUpload}
                  className="hidden"
                  id="report-upload"
                  disabled={uploading || reports.length >= MAX_REPORTS_PER_LEARNER}
                />
                {/*
                <label
                  htmlFor="report-upload"
                  className={`px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer flex items-center ${
                    reports.length >= MAX_REPORTS_PER_LEARNER ||
                    uploading ||
                    (isOwnProfile && (!selectedSemesterId || loadingSemesters))
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                >
                  {uploading ? (
                    <span className="animate-spin mr-2">C</span>
                  ) : (
                    <i className="fas fa-upload mr-2"></i>
                  )}
                  Upload Report
                </label>
                */}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content Body - conditionally rendered */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-slate-100 pt-6">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4">
              {error}
            </div>
          )}
          {exportError && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4">
              {exportError}
            </div>
          )}
          {/*
        {isOwnProfile && (
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-800 mb-2">Assign report to semester</div>
            {loadingSemesters ? (
              <div className="text-sm text-slate-500">Loading semesters...</div>
            ) : semesters.length ? (
              <select
                className="input-brand max-w-md"
                value={selectedSemesterId}
                onChange={(e) => setSelectedSemesterId(e.target.value)}
              >
                <option value="">Select semester before upload</option>
                {semesters.map((semester) => (
                  <option key={semester.id} value={semester.id}>
                    {semester.displayName || semester.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-sm text-slate-500">No semesters found for your account.</div>
            )}
          </div>
        )}
*/}
          <div className="space-y-3">
            {activeReports.length === 0 ? (
              <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
                <i className="fas fa-file-pdf text-4xl mb-3 opacity-50"></i>
                <p>No reports uploaded yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {activeReports.map((report) => (
                  <div
                    key={report.id}
                    className="rounded-xl border p-4 transition-all border-slate-200 bg-slate-50 hover:border-red-200 hover:bg-white"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                          <i className="fas fa-file-pdf text-lg"></i>
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-slate-800 truncate">
                            {report.originalFilename}
                          </h3>
                          <p className="text-xs text-slate-500 mt-1">
                            {(report.fileSizeBytes / 1024).toFixed(1)} KB •{" "}
                            {new Date(report.uploadedAt).toLocaleDateString()}
                            {report.semesterName
                              ? ` • ${report.semesterName}`
                              : ""}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={() => handlePreview(report)}
                        className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:border-red-300 hover:text-red-700 transition-colors"
                      >
                        Preview
                      </button>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDownload(report)}
                          className="p-2 text-slate-500 hover:text-slate-700 hover:bg-white rounded-lg transition-colors"
                          title="Download"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                        {isOwnProfile && (
                          <button
                            onClick={() => handleArchiveClick(report)}
                            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-white rounded-lg transition-colors"
                            title="Archive"
                            aria-label={`Archive ${report.originalFilename}`}
                          >
                            <i className="fas fa-box-archive"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {reports.length >= MAX_REPORTS_PER_LEARNER && isOwnProfile && (
              <div className="text-xs text-amber-600 font-medium text-center mt-2">
                <i className="fas fa-exclamation-circle mr-1"></i>
                Maximum of {MAX_REPORTS_PER_LEARNER} reports reached. Archive older reports to
                upload new ones.
              </div>
            )}

            {archivedReports.length > 0 && (
              <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
                <button
                  onClick={() => setShowArchived((prev) => !prev)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                  aria-expanded={showArchived}
                >
                  <span className="text-sm font-semibold text-slate-700">
                    <i className="fas fa-box-archive mr-2 text-slate-400"></i>
                    Archived reports ({archivedReports.length})
                  </span>
                  <i
                    className={`fas fa-chevron-${showArchived ? "up" : "down"} text-slate-400 text-sm`}
                  />
                </button>

                {showArchived && (
                  <div className="border-t border-slate-200 divide-y divide-slate-100">
                    <p className="px-4 py-2 text-xs text-slate-500">
                      Archived reports stay available to your supervisors and the Revisor.
                    </p>
                    {archivedReports.map((report) => (
                      <div
                        key={report.id}
                        className="px-4 py-3 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-slate-700 truncate">
                            {report.originalFilename}
                          </p>
                          <p className="text-xs text-slate-400">
                            Archived on {new Date(report.archivedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handlePreview(report)}
                            className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:border-slate-300"
                          >
                            Preview
                          </button>
                          {isOwnProfile && (
                            <button
                              onClick={() => handleRestore(report)}
                              className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:border-slate-300"
                            >
                              Restore
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
  
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={closePreview}
        >
          <div
            className="bg-white w-full h-full max-w-5xl max-h-[90vh] rounded-xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="font-bold text-lg text-gray-800 truncate">
                {previewTitle}
              </h3>
              <button
                onClick={closePreview}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            <div className="flex-1 bg-gray-100">
              <iframe
                src={previewUrl}
                className="w-full h-full"
                title="PDF Preview"
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {reportToArchive && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            className="bg-white rounded-2xl shadow-brand-xl border border-pale-dawn w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-box-archive text-slate-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Archive Report
                </h3>
              </div>

              <p className="text-gray-600 mb-2">
                Move{" "}
                <span className="font-semibold text-gray-800">
                  {reportToArchive.originalFilename}
                </span>{" "}
                to your archive?
              </p>
              <p className="text-sm text-gray-500 mb-6">
                The file is not deleted. It stays available to your supervisors and to the
                Revisor, and you can restore it at any time.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setReportToArchive(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                  disabled={isArchiving}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmArchive}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 font-medium transition-colors flex items-center"
                  disabled={isArchiving}
                >
                  {isArchiving && (
                    <span className="animate-spin mr-2 border-2 border-white border-t-transparent rounded-full w-4 h-4 block"></span>
                  )}
                  Archive
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Configuration Modal */}
      <Modal
        isOpen={showExportConfig}
        onClose={closeExportConfig}
        title="Export reports"
        subtitle="Select which reports should be included."
        icon="fa-file-zipper"
        size="md"
        closeOnBackdrop={!isExporting}
        footer={
          <>
            <ModalSecondaryButton onClick={closeExportConfig} disabled={isExporting}>
              Cancel
            </ModalSecondaryButton>
            <ModalPrimaryButton
              onClick={handleExportSelected}
              disabled={selectedReportIds.length === 0}
              loading={isExporting}
              loadingText="Preparing ZIP..."
            >
              Export {selectedReportIds.length}{" "}
              {selectedReportIds.length === 1 ? "report" : "reports"}
            </ModalPrimaryButton>
          </>
        }
      >
        <p className="text-sm text-slate-cloud mb-4">
          Each selected report will be downloaded as a separate file inside one ZIP
          archive.
        </p>

        {exportError && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
            {exportError}
          </div>
        )}

        {activeReports.length === 0 ? (
          <div className="text-center py-6 text-slate-400">
            No reports available to export.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3 text-sm">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedReportIds(activeReports.map((r) => r.id))
                  }
                  className="font-semibold text-sunrise-red hover:underline"
                >
                  Select all
                </button>
                <span className="text-slate-300">|</span>
                <button
                  type="button"
                  onClick={() => setSelectedReportIds([])}
                  className="font-semibold text-sunrise-red hover:underline"
                >
                  Deselect all
                </button>
              </div>
              <span className="text-xs text-slate-500">
                {selectedReportIds.length} of {activeReports.length} selected
              </span>
            </div>

            <div className="space-y-1 max-h-72 overflow-y-auto border border-pale-dawn rounded-xl p-2">
              {activeReports.map((report) => (
                <label
                  key={report.id}
                  className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedReportIds.includes(report.id)}
                    onChange={() => toggleReportSelected(report.id)}
                    className="w-4 h-4 accent-sunrise-red rounded"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {report.originalFilename}
                    </p>
                    <p className="text-xs text-slate-500">
                      {report.semesterName || "No semester"} •{" "}
                      {new Date(report.uploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ReportsList;
